import {
  Breadcrumbs,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  PageContainer,
  Skeleton,
  useApiClient,
} from '@startupquickstart/frontend';
import { useQuery } from '@tanstack/react-query';
import { RefreshCw } from 'lucide-react';
import { useAuth } from '@clerk/clerk-react';
import { useState } from 'react';

interface Organization {
  id: string;
  name: string;
  slug: string | null;
  description: string | null;
  imageUrl: string | null;
  isInternal: boolean;
  role: string;
  permissions: string[];
}

interface UserMeResponse {
  id: string;
  firstName: string | null;
  lastName: string | null;
  email: string;
  clerkId: string | null;
  imageUrl: string | null;
  lastSyncedAt: string | null;
  organizations: Organization[];
}

export function UserMePage() {
  const apiClient = useApiClient();
  const { getToken } = useAuth();
  const [copied, setCopied] = useState(false);

  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: ['User', 'me'],
    queryFn: async () => {
      return apiClient.get<{ success: boolean; data: UserMeResponse }>('/users/me');
    },
  });

  const userData = data?.data;

  const { data: tokenData, isLoading: tokenLoading } = useQuery({
    queryKey: ['auth', 'token'],
    queryFn: async () => {
      const token = await getToken();
      return token;
    },
  });

  const handleRefresh = () => {
    refetch();
  };

  const handleCopyToken = () => {
    if (tokenData) {
      navigator.clipboard.writeText(tokenData);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const breadcrumbs = [
    { label: 'Home', href: '/' },
    { label: 'Current User', current: true },
  ];

  return (
    <PageContainer>
      <Breadcrumbs items={breadcrumbs} className="mb-4" />
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight">Current User</h1>
          <p className="text-muted-foreground mt-1">
            Test endpoint:{' '}
            <code className="text-sm bg-muted px-2 py-1 rounded">GET /api/v1/users/me</code>
          </p>
        </div>
        <button
          onClick={handleRefresh}
          disabled={isFetching}
          className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 disabled:opacity-50"
        >
          <RefreshCw className={`h-4 w-4 ${isFetching ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {error && (
        <Card className="border-destructive mb-6">
          <CardHeader>
            <CardTitle className="text-destructive">Error</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm">
              {error instanceof Error ? error.message : 'Failed to fetch user data'}
            </p>
          </CardContent>
        </Card>
      )}

      {isLoading ? (
        <Card>
          <CardHeader>
            <Skeleton className="h-8 w-48" />
            <Skeleton className="h-4 w-64 mt-2" />
          </CardHeader>
          <CardContent>
            <Skeleton className="h-64 w-full" />
          </CardContent>
        </Card>
      ) : userData ? (
        <div className="space-y-6">
          {/* User Info Card */}
          <Card>
            <CardHeader>
              <CardTitle>User Information</CardTitle>
              <CardDescription>
                {userData.lastSyncedAt
                  ? `Last synced: ${new Date(userData.lastSyncedAt).toLocaleString()}`
                  : 'Not yet synced with Clerk'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-start gap-6 mb-6">
                {userData.imageUrl && (
                  <img
                    src={userData.imageUrl}
                    alt={`${userData.firstName || ''} ${userData.lastName || ''}`}
                    className="w-15 h-15 rounded-full object-cover flex-shrink-0"
                  />
                )}
                <div className="flex-1">
                  <h3 className="text-2xl font-semibold">
                    {userData.firstName || userData.lastName
                      ? `${userData.firstName || ''} ${userData.lastName || ''}`.trim()
                      : 'Unnamed User'}
                  </h3>
                  <p className="text-muted-foreground mt-1">{userData.email}</p>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">User ID</p>
                  <p className="text-sm font-mono">{userData.id}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Clerk ID</p>
                  <p className="text-sm font-mono">{userData.clerkId || 'N/A'}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Access Token Card */}
          <Card>
            <CardHeader>
              <CardTitle>Access Token</CardTitle>
              <CardDescription>Current authentication token for API requests</CardDescription>
            </CardHeader>
            <CardContent>
              {tokenLoading ? (
                <Skeleton className="h-24 w-full" />
              ) : tokenData ? (
                <div className="space-y-3">
                  <pre className="bg-muted p-4 rounded-lg overflow-x-auto text-xs font-mono break-all whitespace-pre-wrap">
                    {tokenData}
                  </pre>
                  <div className="relative inline-block">
                    <button
                      onClick={handleCopyToken}
                      className="text-sm px-3 py-1 bg-secondary text-secondary-foreground rounded hover:bg-secondary/80 cursor-pointer transition-colors"
                    >
                      Copy to Clipboard
                    </button>
                    {copied && (
                      <div className="absolute left-1/2 -translate-x-1/2 -top-10 bg-foreground text-background text-xs px-2 py-1 rounded shadow-lg whitespace-nowrap">
                        Copied to clipboard!
                        <div className="absolute left-1/2 -translate-x-1/2 top-full w-0 h-0 border-l-4 border-r-4 border-t-4 border-l-transparent border-r-transparent border-t-foreground" />
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <p className="text-muted-foreground text-sm">No token available</p>
              )}
            </CardContent>
          </Card>

          {/* Organizations Card */}
          <Card>
            <CardHeader>
              <CardTitle>Organizations</CardTitle>
              <CardDescription>
                {userData.organizations.length === 0
                  ? 'No organizations found'
                  : `Member of ${userData.organizations.length} organization${userData.organizations.length !== 1 ? 's' : ''}`}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {userData.organizations.length > 0 ? (
                <div className="space-y-4">
                  {userData.organizations.map(org => (
                    <div
                      key={org.id}
                      className="border rounded-lg p-4 hover:bg-accent/50 transition-colors"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-4 flex-1">
                          {org.imageUrl && (
                            <img
                              src={org.imageUrl}
                              alt={org.name}
                              className="w-12 h-12 rounded-lg object-cover flex-shrink-0"
                            />
                          )}
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <h3 className="font-semibold text-lg">{org.name}</h3>
                              {org.isInternal && (
                                <span className="text-xs bg-primary/10 text-primary px-2 py-1 rounded">
                                  Internal
                                </span>
                              )}
                            </div>
                            {org.slug && (
                              <p className="text-sm text-muted-foreground mt-1">@{org.slug}</p>
                            )}
                            {org.description && <p className="text-sm mt-2">{org.description}</p>}
                          </div>
                        </div>
                        <div className="text-right ml-4">
                          <span className="text-sm font-medium capitalize bg-accent px-3 py-1 rounded">
                            {org.role}
                          </span>
                        </div>
                      </div>
                      {org.permissions.length > 0 && (
                        <div className="mt-3">
                          <p className="text-xs text-muted-foreground mb-2">Permissions:</p>
                          <div className="flex flex-wrap gap-2">
                            {org.permissions.map(permission => (
                              <span key={permission} className="text-xs bg-muted px-2 py-1 rounded">
                                {permission}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-muted-foreground text-sm">No organizations found</p>
              )}
            </CardContent>
          </Card>

          {/* Raw JSON Card */}
          <Card>
            <CardHeader>
              <CardTitle>Raw JSON Response</CardTitle>
              <CardDescription>Complete API response for debugging</CardDescription>
            </CardHeader>
            <CardContent>
              <pre className="bg-muted p-4 rounded-lg overflow-x-auto text-sm">
                <code>{JSON.stringify(userData, null, 2)}</code>
              </pre>
            </CardContent>
          </Card>
        </div>
      ) : null}
    </PageContainer>
  );
}
